// A schedule time is the host's wall clock: a calendar day plus a time of day ("6:30 PM", or any
// minute, "9:07 PM") read in their IANA zone. The same arithmetic runs in the browser (to grey
// out slots that have gone by) and in the API (to refuse them), so the two never disagree about
// what "already passed" means.
import { SCHEDULE_TIMES } from '../constants.js';

/** "h:mm AM/PM". The three presets are quick picks; any minute of the day is valid. */
export type ScheduleTime = string;

export const SCHEDULE_TIME_RE = /^(1[0-2]|[1-9]):[0-5]\d (AM|PM)$/;

export function isScheduleTime(value: unknown): value is ScheduleTime {
  return typeof value === 'string' && SCHEDULE_TIME_RE.test(value);
}

/** "9:07 PM" → [21, 7]. Throws on anything not in h:mm AM/PM form. */
export function timeToHM(time: ScheduleTime): [number, number] {
  const m = SCHEDULE_TIME_RE.exec(time);
  if (!m) throw new RangeError(`Not a schedule time: ${time}`);
  const [hh, mm] = time.split(' ')[0]!.split(':').map(Number) as [number, number];
  const pm = m[2] === 'PM';
  const h = hh === 12 ? (pm ? 12 : 0) : pm ? hh + 12 : hh;
  return [h, mm];
}

/** [21, 7] → "9:07 PM": the form the API, the database and the pickers all use. */
export function formatScheduleTime(h: number, m: number): ScheduleTime {
  const pm = h >= 12;
  const hh = h % 12 === 0 ? 12 : h % 12;
  return `${hh}:${String(m).padStart(2, '0')} ${pm ? 'PM' : 'AM'}`;
}

type Wall = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
};

const wallFormatters = new Map<string, Intl.DateTimeFormat>();

/** The wall clock an instant shows in a zone, read off Intl (the runtime's own tz database). */
function wallClockIn(instant: Date, timezone: string): Wall {
  let fmt = wallFormatters.get(timezone);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    wallFormatters.set(timezone, fmt);
  }
  const wall: Wall = { year: 0, month: 0, day: 0, hour: 0, minute: 0, second: 0 };
  for (const p of fmt.formatToParts(instant)) {
    if (p.type in wall) wall[p.type as keyof Wall] = Number(p.value);
  }
  // Some engines print midnight as 24 under h23.
  wall.hour %= 24;
  return wall;
}

/** Milliseconds the zone is ahead of UTC at that instant (negative west of Greenwich). */
function zoneOffsetMs(instant: Date, timezone: string): number {
  const w = wallClockIn(instant, timezone);
  return Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, w.second) - instant.getTime();
}

/**
 * Combine a yyyy-mm-dd + time of day into the instant that wall clock happens in the host's zone.
 * Without a zone (older clients) the wall clock is read as UTC, as it always was.
 */
export function scheduleDate(date: string, time: ScheduleTime, timezone?: string | null): Date {
  const [h, m] = timeToHM(time);
  return wallInstant(date, h, m, timezone);
}

/** The instant a wall clock (yyyy-mm-dd, h:m) happens in a zone; read as UTC without one. */
export function wallInstant(date: string, h: number, m: number, timezone?: string | null): Date {
  const [y, mo, d] = date.split('-').map(Number) as [number, number, number];
  const wall = Date.UTC(y, mo - 1, d, h, m, 0);
  if (!timezone) return new Date(wall);
  // Guess with the offset in force at the wall time read as UTC, then check the offset at the
  // guessed instant: across a DST switch the two differ and the second one is usually right.
  const guess = wall - zoneOffsetMs(new Date(wall), timezone);
  const offset = zoneOffsetMs(new Date(guess), timezone);
  const alt = wall - offset;
  if (alt === guess) return new Date(guess);
  if (showsWall(new Date(alt), timezone, h, m)) return new Date(alt);
  // A wall clock that never happens (the spring-forward gap): take the later instant, so a post
  // asked for 2:30 goes out at 3:30 rather than an hour before its time.
  return new Date(Math.max(guess, alt));
}

function showsWall(instant: Date, timezone: string, h: number, m: number): boolean {
  const w = wallClockIn(instant, timezone);
  return w.hour === h && w.minute === m;
}

/** Midnight of a calendar day in a zone (UTC without one). */
export function dayStart(date: string, timezone?: string | null): Date {
  return wallInstant(date, 0, 0, timezone);
}

/** A calendar day (yyyy-mm-dd) moved by whole days, no zone involved. */
export function shiftDay(date: string, days: number): string {
  const [y, mo, d] = date.split('-').map(Number) as [number, number, number];
  return isoDate(new Date(Date.UTC(y, mo - 1, d + days)));
}

/** The calendar day (yyyy-mm-dd) an instant falls on in a zone; UTC when none is given. */
export function isoDateIn(d: Date, timezone?: string | null): string {
  if (!timezone) return isoDate(d);
  const w = wallClockIn(d, timezone);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${w.year}-${p(w.month)}-${p(w.day)}`;
}

export function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/** True once a slot's instant is not after `now`: it has gone by on the host's clock. */
export function slotHasPassed(
  date: string,
  time: ScheduleTime,
  timezone: string | null | undefined,
  now: Date,
): boolean {
  return scheduleDate(date, time, timezone).getTime() <= now.getTime();
}

/**
 * The pick as given when it is still ahead (a preset or any minute); otherwise the next preset
 * left on that day, else the first preset of the next day that still has one. A day already
 * over lands on today.
 */
export function firstOpenSlot(
  date: string,
  time: ScheduleTime,
  timezone: string | null | undefined,
  now: Date,
): { date: string; time: ScheduleTime } {
  if (!slotHasPassed(date, time, timezone, now)) return { date, time };
  const today = isoDateIn(now, timezone);
  let d = date < today ? today : date;
  for (let i = 0; i < 3; i++) {
    for (const t of SCHEDULE_TIMES)
      if (!slotHasPassed(d, t, timezone, now)) return { date: d, time: t };
    d = shiftDay(d, 1);
  }
  return { date: d, time: SCHEDULE_TIMES[0] };
}

/** The last minute a day can take a post: "today" lasts until this goes by on the property clock. */
const LAST_MINUTE: ScheduleTime = '11:59 PM';

/** True once a whole day has gone by in a zone; today is not over while any minute is ahead. */
export function isDayOver(date: string, timezone: string | null | undefined, now: Date): boolean {
  return slotHasPassed(date, LAST_MINUTE, timezone, now);
}

/** Short weekday names, Monday first: the form `properties.best_time` is written in. */
export const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

/** The weekday of a calendar day (yyyy-mm-dd), Monday = 0 … Sunday = 6. No zone involved. */
export function weekdayOf(date: string): number {
  const [y, mo, d] = date.split('-').map(Number) as [number, number, number];
  return (new Date(Date.UTC(y, mo - 1, d)).getUTCDay() + 6) % 7;
}

/** "Thu 6:30 PM" → weekday (Mon = 0) and preset time; Monday and 6:30 PM on what it cannot read. */
export function parseBestTime(best: string): { day: number; time: ScheduleTime } {
  const [d = 'Thu', ...rest] = best.split(' ');
  const day = Math.max(0, (WEEKDAYS as readonly string[]).indexOf(d));
  const t = rest.join(' ');
  const time = (SCHEDULE_TIMES as readonly string[]).includes(t) ? t : '6:30 PM';
  return { day, time };
}
